/**
 * Canonical tactics-board state — used by both the 3D board
 * (apps/web2 TacticsBoard3D) and the upcoming 2D board (Phase B in
 * docs/scope/session-and-2d-planner.md).
 *
 * Coordinate system: world metres, pitch-centred origin.
 *   x ∈ [-FIELD_WIDTH/2,  +FIELD_WIDTH/2]   (sidelines)
 *   z ∈ [-FIELD_LENGTH/2, +FIELD_LENGTH/2]  (-Z = away end / attacking goal,
 *                                            +Z = home end / own goal)
 *   y is unused at the layout level (it's the figure-render axis in 3D
 *   only; 2D ignores it).
 *
 * This shape is the source of truth that both renderers consume. The
 * static `<Pitch>` SVG component renders a derived pixel-space view via
 * `toPitchPositions` (./adapters.ts).
 *
 * Schema versioned so future shape changes can no-op stale entries
 * instead of crashing on missing fields.
 */

export const TACTICS_SCHEMA_VERSION = 5;

/** A player token's resting position. Role is the formation slot
 *  (GK / CB / LW / etc.); identity is implied by the (team, index)
 *  pair the renderer uses for keying. */
export interface TacticsPlayerSpot {
  /** Formation slot — drives archetype mapping in the 3D renderer. */
  role: string;
  /** World metres, pitch-centred. */
  x: number;
  /** Y-axis ignored by the 2D renderer; 3D uses it for figure height. */
  y: number;
  /** World metres, pitch-centred. */
  z: number;
}

/** A movement arrow on the pitch. When `playerKey` is set, the arrow
 *  binds to that token and playback animates them along the curve. */
export interface TacticsArrow {
  id: string;
  /** ≥2 control points in world metres along the curve. */
  points: { x: number; z: number }[];
  /** CSS hex colour for the tube + tip. */
  color: string;
  /** Optional player binding ("home-3" / "away-7"). When set, the
   *  arrow is a movement run for that token; absent = passive
   *  annotation. */
  playerKey?: string;
}

// ---------------------------------------------------------------------------
// Phase 2 — sequencing + branching scenarios
// ---------------------------------------------------------------------------

/** A named moment within a tactical play. Replaces the v=1 single-
 *  snapshot model: a layout becomes a sequence of keyframes, each
 *  with its own positions + arrows, parameterised by `t ∈ [0, 1]`.
 *  Playback interpolates positions across keyframes; arrows are
 *  re-emitted per keyframe so the lines change as the play unfolds. */
export interface TacticsKeyframe {
  id: string;
  /** Position along the sequence in [0, 1]. */
  t: number;
  /** Human-readable label — 'Build-up', 'Switch', 'Final third', etc. */
  label: string;
  homeSpots: TacticsPlayerSpot[];
  awaySpots: TacticsPlayerSpot[];
  arrows: TacticsArrow[];
  /** Phase-of-play this keyframe belongs to — `'attacking'` /
   *  `'defending'` / `'transitions'` / `'individual'`. Drives the
   *  phase-coloured tick + group label on the keyframe scrubber so a
   *  6-keyframe play reads as `Build-up → Progression → Finish`
   *  visually. Optional — undefined keyframes render with a neutral
   *  tick. Stored as a string so the core schema doesn't take a
   *  dependency on the phase-of-play module shape. */
  phase?: 'attacking' | 'defending' | 'transitions' | 'individual';
}

/** An alternate branch off a keyframe — "if pass intercepted", "if
 *  press wins it", etc. Its keyframes play as a sub-sequence; the
 *  coach picks a branch from the HUD or the Brain. */
export interface TacticsBranch {
  id: string;
  /** Keyframe this branch forks off. */
  parentKeyframeId: string;
  /** Coach-visible label — "if pass intercepted". */
  trigger: string;
  keyframes: TacticsKeyframe[];
}

// ---------------------------------------------------------------------------
// Phase 3 — annotation + teaching layer
// ---------------------------------------------------------------------------

/** Coloured zone on the pitch — press trap, cover shadow, attacking
 *  channel, etc. Geometry is in world metres. */
export interface TacticsZone {
  id: string;
  shape: 'rect' | 'circle' | 'polygon';
  /** Geometry depends on shape:
   *  - rect: [cx, cz, width, depth]
   *  - circle: [cx, cz, radius]
   *  - polygon: flat [x, z, x, z, ...] in winding order */
  geom: number[];
  /** CSS hex/rgba fill — opacity baked in. */
  fill: string;
  /** Optional in-pitch label. */
  label?: string;
}

/** Coaching-point bubble. Pins to a pitch position; optionally ties
 *  to a specific keyframe so it appears only at that moment. */
export interface TacticsCoachingPoint {
  id: string;
  at: { x: number; z: number };
  text: string;
  keyframeId?: string;
}

/** Constraint card — "must complete 3 passes in this zone before
 *  switching." Optionally pinned to a zone. */
export interface TacticsConstraint {
  id: string;
  text: string;
  zoneId?: string;
}

/** Per-keyframe voice-over. Audio URL is optional (grassroots tier
 *  defaults to transcript-only); transcript is required for player
 *  accessibility + future AI parsing. */
export interface TacticsVoiceover {
  keyframeId: string;
  audioUrl?: string;
  transcript: string;
}

// ---------------------------------------------------------------------------
// Phase 4 — roles, team tactics, set pieces, drawings, custom formations, overlays
// ---------------------------------------------------------------------------
//
// Each of the field-shaped types below is intentionally permissive
// (`Record<string, unknown>` or shallow primitive bags). The fully
// typed shapes live in feature modules (e.g. `./roles.ts`,
// `./teamTactics.ts`, `./drawings.ts`) so the schema header stays
// stable as those modules iterate. Anything saved against a more-
// specific shape narrows on read via that module's type guards.

/** Per-token role + instruction selections (§5). Keys are `team-index`
 *  ("home-3" / "away-7"); values are role + instruction bags defined
 *  in `./roles.ts`. */
export type TacticsPlayerRolesByKey = Record<string, unknown>;

/** Team-wide tactical config (§6): build-up style, defensive approach,
 *  width / depth / tempo sliders, 5 tactic slots. Shape defined in
 *  `./teamTactics.ts`. */
export interface TacticsTeamTacticsBundle {
  home?: unknown;
  away?: unknown;
}

/** Set-piece play (§6.5): corners, free kicks, throw-ins, penalties.
 *  Each carries its own positions[] + runs[] + delivery metadata.
 *  Shape defined in `./setPieces.ts`. */
export type TacticsSetPiece = Record<string, unknown> & { id: string };

/** Drawing primitives layered on top of the pitch (§7): straight /
 *  dashed / curved lines, multiple arrow heads, shapes, text. Shape
 *  defined in `./drawings.ts`. */
export type TacticsDrawing = Record<string, unknown> & { id: string };

/** User-saved custom formation (§3): name + 11 role positions. Shape
 *  defined in `./formationsExtended.ts`. */
export type TacticsCustomFormation = Record<string, unknown> & { id: string };

/** Analytical overlay toggles (§17): Voronoi / heatmap / pass net /
 *  pitch control. Shape defined in `./overlays.ts`. */
export type TacticsOverlaysState = Record<string, unknown>;

/** v=5 — per-player tactical brief. Shape defined in `./playerBriefs.ts`. */
export type TacticsPlayerBriefsByKey = Record<string, unknown>;

/** v=5 — per-player roster identity. Keyed by playerKey ("home-3").
 *  Empty / undefined entries fall back to "#N" by index — coaches don't
 *  have to fill every slot for the feature to be useful. */
export interface TacticsRosterEntry {
  name?: string;
  number?: number;
}
export type TacticsRoster = Record<string, TacticsRosterEntry>;

// ---------------------------------------------------------------------------
// Composite layout
// ---------------------------------------------------------------------------

/** Persisted tactics layout — what gets saved per session under a
 *  `persistKey` (e.g. `tactics-3d-${sessionId}`).
 *
 *  Schema evolution:
 *  - v=1: homeFormation / awayFormation / homeSpots / awaySpots / arrows
 *  - v=2: + keyframes? + branches?
 *  - v=3: + zones? + coachingPoints? + constraints? + voiceovers?
 *  - v=4: + playerRoles? + teamTactics? + setPieces? + drawings?
 *         + customFormations? + overlays?
 *  - v=5: + playerBriefs? + playerBriefsStale?
 *
 *  Every Phase 2/3/4/5 field is optional — v=1 entries load unchanged.
 *  The store's read path upcasts old entries by populating only the
 *  v=1 fields and leaving the rest undefined. */
export interface TacticsLayout {
  /** Schema version; bump on incompatible changes. */
  v: number;
  /** Formation choice at save time. */
  homeFormation: string;
  awayFormation: string;
  /** Per-team token positions. */
  homeSpots: TacticsPlayerSpot[];
  awaySpots: TacticsPlayerSpot[];
  /** Drawn arrows. */
  arrows: TacticsArrow[];
  /** Phase 2 — keyframes for multi-moment sequencing. */
  keyframes?: TacticsKeyframe[];
  /** Phase 2 — alternate branches rooted at specific keyframes. */
  branches?: TacticsBranch[];
  /** Phase 3 — coloured zones overlaid on the pitch. */
  zones?: TacticsZone[];
  /** Phase 3 — coaching-point bubbles pinned to pitch positions. */
  coachingPoints?: TacticsCoachingPoint[];
  /** Phase 3 — constraint cards (drill rules / restrictions). */
  constraints?: TacticsConstraint[];
  /** Phase 3 — per-keyframe voice-overs. */
  voiceovers?: TacticsVoiceover[];
  /** Phase 4 — per-token role + instruction selections (§5). */
  playerRoles?: TacticsPlayerRolesByKey;
  /** Phase 4 — team-wide tactical sliders + 5 tactic slots (§6). */
  teamTactics?: TacticsTeamTacticsBundle;
  /** Phase 4 — set-piece tactics (§6.5). */
  setPieces?: TacticsSetPiece[];
  /** Phase 4 — drawing-toolbar primitives stacked on the pitch (§7). */
  drawings?: TacticsDrawing[];
  /** Phase 4 — coach-saved custom formations (§3). */
  customFormations?: TacticsCustomFormation[];
  /** Phase 4 — analytical overlay toggles (§17). */
  overlays?: TacticsOverlaysState;
  /** v=5 — per-player tactical briefs keyed by playerKey
   *  (`home-3` / `away-7`). Values conform to `PlayerBrief` from
   *  `./playerBriefs.ts`; held as `unknown` here so the schema header
   *  doesn't depend on the brief module. */
  playerBriefs?: TacticsPlayerBriefsByKey;
  /** v=5 — set to true whenever the layout's brief-affecting inputs
   *  change after briefs were last generated, so the drawer can prompt
   *  the coach to regenerate. Auto-managed by `saveTacticsLayout`. */
  playerBriefsStale?: boolean;
  /** v=5 — per-player edit history. Stack of previous PlayerBrief
   *  versions keyed by playerKey, newest first, capped at 5 entries
   *  per player. The drawer surfaces these so the coach can roll a
   *  brief back to a prior wording. Shape defined alongside
   *  `playerBriefs` in `./playerBriefs.ts`. */
  playerBriefHistory?: Record<string, unknown>;
  /** v=5 — coach-supplied roster identities keyed by playerKey
   *  ("home-3" / "away-7"). Each entry can carry a name and / or
   *  jersey number. Tokens + briefs render initials / numbers from
   *  this when present; otherwise fall back to the index. */
  roster?: TacticsRoster;
  /** Last save timestamp (ISO). Surfaced in HUD strings like "saved 2m ago". */
  saved_at: string;
}
